import _asyncToGenerator from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _regeneratorRuntime from "/workdir/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '@segment/matcha/components/Input';
import TextArea from '@segment/matcha/components/TextArea';
import Select from '@segment/matcha/components/Select';
import useSelect from '@segment/matcha/components/Select/useSelect';
import { ChevronDownIcon } from '@segment/matcha/components/Icons/Common';
import { CompanyEmailField, companyEmailMessages, CompanyNameField, companyNameMessages } from '@segment/templates/FormFields';
import FormSubmitAction from '@segment/templates/FormSubmitAction';
import TrackableForm, { errorMessage } from '@segment/templates/TrackableForm';
import { trackIntegrationRequestForm } from '@segment/tracking/scripts/helpers';
var textRegex = /^[a-zA-Z]/;
import useScrollToAnchor from '@segment/utils/useScrollToAnchor';
import useMatchMedia from '@segment/utils/useMatchMedia';
import sharedEmailRegex from '@segment/utils/regexes/sharedEmail';
import emailRegex from '@segment/utils/regexes/email';
import formRegex from '@segment/utils/regexes/form';
import * as views from './RequestIntegration.views';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
var successMessage = {
  heading: "Thanks!",
  description: "Your request has been received."
};
var schema = z.object({
  toolName: z.string().min(1, {
    message: "Please provide your tool name."
  })
  /* uses textRegex */
  ,
  toolType: z // eslint-disable-next-line camelcase
  .string({
    required_error: "Please provide a valid tool type."
  }).regex(formRegex, {
    message: "Please provide a valid tool type."
  }),
  useCase: z.string().regex(formRegex, {
    message: "Please provide a valid use case."
  }),
  email: z.string().min(1, {
    message: companyEmailMessages.required
  }).regex(formRegex, {
    message: companyEmailMessages.invalid
  }).regex(emailRegex, {
    message: companyEmailMessages.invalid
  })
  /* This is equal to .email( { message: companyEmailMessages.invalid }) but with a custom regex */
  .regex(sharedEmailRegex, {
    message: companyEmailMessages.sharedEmail
  }),
  company: z.string().min(1, {
    message: companyNameMessages.required
  }).regex(formRegex, {
    message: companyNameMessages.invalid
  })
});
export var RequestIntegration = function RequestIntegration(_ref) {
  var _formState$errors$too, _formState$errors$too2, _formState$errors$use;

  var after = _ref.after;
  var theme = useTheme();
  var formRef = useRef(null);
  var methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: {
      toolType: "Source"
    }
  });
  var register = methods.register,
      setValue = methods.setValue,
      formState = methods.formState;
  var options = [{
    value: "source",
    text: "Source"
  }, {
    value: "destination",
    text: "Destination"
  }];

  var _useSelect = useSelect({
    initialSelected: [options[0]],
    options: options,
    id: "integrationSelect",
    onSelect: function onSelect() {
      return setValue("toolType", state.selected[0].text);
    },
    appearance: "twilioLight"
  }),
      state = _useSelect.state,
      getControlProps = _useSelect.getControlProps,
      filteredOptions = _useSelect.filteredOptions,
      getSelectProps = _useSelect.getSelectProps;

  var _useState = useState(false),
      valid = _useState[0],
      setValid = _useState[1];

  var scrollTo = useScrollToAnchor("request-integration-form", -96); // Mobile breakpoints

  var mobileResolutionMediaQuery = theme.breakpoints.down("md", false);
  var isSmallResolution = useMatchMedia(mobileResolutionMediaQuery, true);
  var toolTypeFormProps = register("toolType");

  var controlProps = _objectSpread(_objectSpread({}, toolTypeFormProps), getControlProps({
    placeholder: "Select an option...",
    onChange: toolTypeFormProps.onChange
  }));

  return _jsx(views.FormWrapper, {
    "data-testid": "request-integration-form",
    id: "request-integration-form",
    ref: formRef,
    children: _jsxs(TrackableForm, {
      gap: "small",
      methods: methods,
      onSubmit: /*#__PURE__*/function () {
        var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(data) {
          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _context.next = 2;
                  return trackIntegrationRequestForm(data);

                case 2:
                  if (isSmallResolution) scrollTo();

                case 3:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee);
        }));

        return function (_x) {
          return _ref2.apply(this, arguments);
        };
      }(),
      success: _jsx(FormSubmitAction, _objectSpread({
        status: "success"
      }, successMessage)),
      error: _jsx(FormSubmitAction, _objectSpread({
        status: "error"
      }, errorMessage)),
      after: after,
      children: [_jsx(Input, _objectSpread({
        id: "toolName",
        label: "What\u2019s the name of the tool you\u2019re looking for?",
        placeholder: "e.g. Google Analytics",
        error: Boolean(formState.errors.toolName),
        showValidState: valid && Boolean(!formState.errors.toolName),
        errorText: (_formState$errors$too = formState.errors.toolName) === null || _formState$errors$too === void 0 ? void 0 : _formState$errors$too.message,
        "data-testid": "toolName"
      }, register("toolName", {
        required: "Please provide the name of the tool you\u2019re looking for.",
        pattern: textRegex,
        onBlur: function onBlur(e) {
          setValid(textRegex.test(e.target.value));
        }
      }))), _jsxs(views.SelectWrapper, {
        children: [_jsx(Input, _objectSpread({
          id: "toolType",
          type: "search",
          label: "Do you want to get data from this tool (Source) or do you want to send data to this tool (Destination)?",
          resetable: state.filterValue !== "",
          rightSecondIcon: _jsx(ChevronDownIcon, {
            color: theme.palette.twilio.gray[100]
          }),
          error: Boolean(formState.errors.toolType),
          errorText: (_formState$errors$too2 = formState.errors.toolType) === null || _formState$errors$too2 === void 0 ? void 0 : _formState$errors$too2.message,
          "data-testid": "toolType"
        }, controlProps)), _jsx(Select, _objectSpread({
          id: "toolSourceDestinationSelect",
          name: "toolSourceDestinationSelect",
          options: options,
          filteredOptions: filteredOptions,
          displayMode: state.displayMode,
          open: state.open
        }, getSelectProps()))]
      }), _jsx(TextArea, _objectSpread({
        id: "useCase",
        label: "Why is this tool important for you? What problem(s) would it solve?",
        placeholder: "I need this to be an integration because...",
        error: Boolean(formState.errors.useCase),
        errorText: (_formState$errors$use = formState.errors.useCase) === null || _formState$errors$use === void 0 ? void 0 : _formState$errors$use.message,
        rows: 4,
        "data-testid": "useCase"
      }, register("useCase", {
        required: "Please provide the reason why this tool is important for you.",
        pattern: textRegex
      }))), _jsx(CompanyEmailField, {
        label: "What\u2019s your work email?",
        "data-testid": "email"
      }), _jsx(CompanyNameField, {
        label: "What\u2019s your company name?",
        "data-testid": "company"
      }), _jsx(views.ButtonWrapper, {
        color: "primary",
        variant: "filled",
        size: "large",
        children: "Submit request"
      })]
    })
  });
};